import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaSave } from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import DynamicIcon from '../Common/DynamicIcon';
import { FaUpload } from 'react-icons/fa';

const CATEGORY_ICONS = [
  'Baby', 'Users', 'Sparkles', 'BookOpen', 'Dog', 'Hammer', 'Car', 'Stethoscope', 
  'Leaf', 'Heart', 'Shield', 'Calendar', 'Map', 'User', 'Wallet', 
  'Home', 'ShoppingBag', 'Mic', 'Camera', 'Music', 'Coffee', 'Utensils',
  'Droplet', 'Zap', 'Wind', 'Scissors', 'PenTool', 'GraduationCap'
];

const CategoryModal = ({ isOpen, onClose, category = null, refreshCategories }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    slug: '',
    icon: 'Package',
    image: '',
    isActive: true
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  // Helper to resolve image URL
  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const baseUrl = (api.defaults.baseURL || '').replace('/api', '');
    return `${baseUrl}${path}`;
  };

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        slug: category.slug || '',
        icon: category.icon || '',
        image: category.image || '',
        isActive: category.isActive !== undefined ? category.isActive : true
      });
      setPreviewUrl(category.image ? getImageUrl(category.image) : '');
    } else {
      setFormData({
        name: '',
        description: '',
        slug: '',
        icon: '',
        image: '',
        isActive: true
      });
      setSelectedFile(null);
      setPreviewUrl('');
    }
  }, [category, isOpen]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      // STRICT PRIORITY: Clear icon if image is uploaded
      setFormData(prev => ({ ...prev, icon: '', image: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append('name', formData.name);
    data.append('description', formData.description || '');
    data.append('slug', formData.slug || '');
    data.append('isActive', formData.isActive);

    // LOGIC: Image takes Priority 1, Icon is Priority 2
    if (selectedFile) {
      // 1. New File Uploaded
      data.append('image', selectedFile);
      data.append('icon', ''); 
    } else if (previewUrl && !formData.icon) {
      // 2. Keeping Existing Image (previewUrl exists, no new file, and icon not chosen)
      data.append('image', formData.image);
      data.append('icon', '');
    } else if (formData.icon) {
      // 3. Icon Chosen (Explicitly clear image)
      data.append('icon', formData.icon);
      data.append('image', ''); 
    } else {
      // 4. Neither chosen
      data.append('icon', '');
      data.append('image', '');
    }

    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (category) {
        await api.patch(`${ENDPOINTS.SERVICES.CATEGORIES}/${category.id}`, data, config);
        toast.success('Category updated successfully');
      } else {
        await api.post(ENDPOINTS.SERVICES.CATEGORIES, data, config);
        toast.success('Category created successfully');
      }
      refreshCategories();
      onClose();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error('Failed to save category');
    }
  };

  if (!isOpen) return null;
  
  const modalContent = (
    <div className="category-modal-overlay">
      <div className="category-modal-content">
        <div className="modal-header">
          <h3>{category ? 'Edit Category' : 'Add New Category'}</h3>
          <button className="btn-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form-container" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <div className="modal-form-body" style={{ padding: '28px', overflowY: 'auto', flex: 1 }}>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
            <div className="form-group">
              <label>Slug (Optional)</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                className="form-control"
                placeholder="e.g. home-cleaning"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-control"
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>Category Icon or Custom Image (Optional)</label>
              <div className="category-visual-options">
                <div className="icon-picker-section">
                  <span>Choose an Icon:</span>
                  <div className="icon-picker-grid">
                    {CATEGORY_ICONS.map(iconName => (
                      <div 
                        key={iconName}
                        className={`icon-picker-item ${formData.icon === iconName ? 'selected' : ''}`}
                        onClick={() => {
                          setFormData({ ...formData, icon: iconName, image: '' });
                          setSelectedFile(null);
                          setPreviewUrl('');
                        }}
                        title={iconName}
                      >
                        <DynamicIcon name={iconName} size={20} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="image-upload-section">
                  <span>Or Upload Custom Image:</span>
                  <div className="image-upload-controls">
                    <label className="image-upload-label">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      <div className="upload-placeholder">
                        <FaUpload />
                        <span>{selectedFile || previewUrl ? 'Change Image' : 'Upload Image'}</span>
                      </div>
                    </label>
                    {previewUrl && (
                      <div className="image-preview-container">
                        <img src={previewUrl} alt="Preview" className="category-image-preview" />
                        <button 
                          type="button" 
                          className="btn-remove-image" 
                          onClick={() => {
                            setSelectedFile(null);
                            setPreviewUrl('');
                            setFormData(prev => ({ ...prev, image: '' }));
                          }}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="visual-preview-summary">
                <span>Final Preview:</span>
                <div className="selected-visual-preview">
                  {previewUrl ? (
                    <img 
                      src={previewUrl} 
                      alt="Selected" 
                      className="preview-img-square" 
                    />
                  ) : formData.icon ? (
                    <DynamicIcon name={formData.icon} size={32} />
                  ) : (
                    <div className="no-preview">No Icon/Image</div>
                  )}
                </div>
              </div>
            </div>
            <div className="form-group">
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                  margin: 0
                }}
              >
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  style={{
                    margin: 0,
                    width: "16px",
                    height: "16px"
                  }}
                />
                <span style={{ margin: 0 }}>Active</span>
              </label>
            </div>
          </div>
          <div className="modal-footer" style={{ justifyContent: 'space-between', padding: '20px 28px', flexShrink: 0 }}>
            <button type="button" className="btn-cancel-text" onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', fontWeight: '500', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" style={{ minWidth: '140px' }}>
              <FaSave /> Save Category
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CategoryModal;
